import React, { FC, ReactElement, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import { useErrorController } from "@natera/platform/lib/hooks";
import { Form, FormField, Textfield, Button } from "@natera/form";
import { useMdcSelectScrollFixAll } from "@app/preBill/hooks";

import { ButtonNoShadow } from "@app/components/buttonNoShadow";
import { ConfigContext } from "@app/provider";
import { routes } from "@app/routing";
import { DOBFormValidateDay, validateYear } from "@app/utils/dateHelper";
import { PublicGuestValidationInput } from "@app/postBill/models";
import ChipAction from "@app/postBill/components/chipAction/chipAction";

import "./billingCenterGuestVerification.scss";
import DateOfBirthVerificationForm, {
  Month,
} from "@app/preBill/components/dateOfBirthVerification/dateOfBirthVerificationForm";

const messages = defineMessages({
  billingCenterGuestVerificationButtonBack: {
    id: "billingCenterGuestVerificationButtonBack",
    defaultMessage: "Back",
  },
  billingCenterGuestVerificationButtonContinue: {
    id: "billingCenterGuestVerificationButtonContinue",
    defaultMessage: "Continue",
  },
  billingCenterGuestVerificationButtonLogin: {
    id: "billingCenterGuestVerificationButtonLogin",
    defaultMessage: "I already have an account",
  },
  billingCenterGuestVerificationCaseNumberLink: {
    id: "billingCenterGuestVerificationCaseNumberLink",
    defaultMessage: "I don't remember my case number",
  },
  billingCenterGuestVerificationCaseNumberTooltip: {
    id: "billingCenterGuestVerificationCaseNumberTooltip",
    defaultMessage: "This can be found on your statement",
  },
  billingCenterGuestVerificationLabelCaseNumber: {
    id: "billingCenterGuestVerificationLabelCaseNumber",
    defaultMessage: "Case Number",
  },
  billingCenterGuestVerificationLabelCaseNumberPlaceholder: {
    id: "billingCenterGuestVerificationLabelCaseNumberPlaceholder",
    defaultMessage: "Enter your case number",
  },
  billingCenterGuestVerificationLabelFirstName: {
    id: "billingCenterGuestVerificationLabelFirstName",
    defaultMessage: "First Name",
  },
  billingCenterGuestVerificationLabelLastName: {
    id: "billingCenterGuestVerificationLabelLastName",
    defaultMessage: "Last Name",
  },
  billingCenterGuestVerificationTypeEmailPlaceholder: {
    id: "billingCenterGuestVerificationTypeEmailPlaceholder",
    defaultMessage: "Email",
  },
  billingCenterGuestVerificationTypeLabel: {
    id: "billingCenterGuestVerificationTypeLabel",
    defaultMessage: "Please enter one of the following information:",
  },
  billingCenterGuestVerificationTypeMobileNumberPlaceholder: {
    id: "billingCenterGuestVerificationTypeMobileNumberPlaceholder",
    defaultMessage: "Mobile number",
  },
  billingCenterGuestVerificationTypeZipCodePlaceholder: {
    id: "billingCenterGuestVerificationTypeZipCodePlaceholder",
    defaultMessage: "Zip code",
  },
  dateOfBirthVerificationInvalidDay: {
    id: "dateOfBirthVerificationInvalidDay",
    defaultMessage: "Invalid or incomplete value.",
  },
  dateOfBirthVerificationInvalidYear: {
    id: "dateOfBirthVerificationInvalidYear",
    defaultMessage: "Incorrect date of birth. Please re-enter",
  },
  dateOfBirthVerificationEmptyValue: {
    id: "dateOfBirthVerificationEmptyValue",
    defaultMessage: "This information is required",
  },
  guestDataVerificationInvalidCaseNumber: {
    id: "guestDataVerificationInvalidCaseNumber",
    defaultMessage: "Please enter Case Number",
  },
  guestDataVerificationInvalidFirstName: {
    id: "guestDataVerificationInvalidFirstName",
    defaultMessage: "Please enter First Name",
  },
  guestDataVerificationInvalidLastName: {
    id: "guestDataVerificationInvalidLastName",
    defaultMessage: "Please enter Last Name",
  },
  guestDataVerificationInvalidZipCode: {
    id: "guestDataVerificationInvalidZipCode",
    defaultMessage: "Please enter Zip Code",
  },
  guestDataVerificationInvalidMobileNumber: {
    id: "guestDataVerificationInvalidMobileNumber",
    defaultMessage: "Please enter Mobile Number",
  },
  guestDataVerificationInvalidEmail: {
    id: "guestDataVerificationInvalidEmail",
    defaultMessage: "Please enter Email",
  },
});

interface BillingCenterGuestVerificationProps {
  verificationType?: VerificationType;
  loading: boolean;
  setVerificationType: React.Dispatch<React.SetStateAction<VerificationType>>;
  onSubmit: (data: PublicGuestValidationInput) => void;
}

export enum VerificationType {
  CASE_NUMBER = "caseNumber",
  ZIPCODE = "zipCode",
  MOBILE_NUMBER = "mobileNumber",
  EMAIL = "email",
}

const BillingCenterGuestVerification: FC<
  BillingCenterGuestVerificationProps
> = ({
  verificationType = VerificationType.CASE_NUMBER,
  loading,
  setVerificationType,
  onSubmit,
}) => {
  useMdcSelectScrollFixAll();
  const intl = useIntl();
  const navigate = useNavigate();
  const { config } = useContext(ConfigContext);
  const {
    getValidationError,
    setValidationError,
    clearValidationError,
    clearErrors,
  } = useErrorController();
  const [month, setMonth] = useState<Month | null>(null);
  const [day, setDay] = useState<string>("");
  const [year, setYear] = useState<string>("");

  const setFieldError = (field: string, messageKey: keyof typeof messages) => {
    setValidationError(field, intl.formatMessage(messages[messageKey]));
  };

  const validateDOB = (day: string, month: string, year: string): boolean => {
    if (month === "") {
      setFieldError("dateOfBirthMonth", "dateOfBirthVerificationEmptyValue");
      return false;
    }
    if (day === "") {
      setFieldError("dateOfBirthDay", "dateOfBirthVerificationEmptyValue");
      return false;
    }
    if (year === "") {
      setFieldError("dateOfBirthYear", "dateOfBirthVerificationEmptyValue");
      return false;
    }
    if (!validateYear(year)) {
      setFieldError("dateOfBirthYear", "dateOfBirthVerificationInvalidYear");
      return false;
    }
    if (!DOBFormValidateDay(day, parseInt(month, 10), year)) {
      setFieldError("dateOfBirthDay", "dateOfBirthVerificationInvalidDay");
      return false;
    }
    return true;
  };

  const validateAdditionalUserData = (
    caseNumber: string,
    firstName: string,
    lastName: string,
    zipCode: string,
    mobileNumber: string,
    email: string
  ): boolean => {
    if (verificationType !== VerificationType.CASE_NUMBER) {
      if (firstName === "") {
        setFieldError("firstName", "guestDataVerificationInvalidFirstName");
        return false;
      }
      if (lastName === "") {
        setFieldError("lastName", "guestDataVerificationInvalidLastName");
        return false;
      }
      if (verificationType === VerificationType.ZIPCODE && zipCode === "") {
        setFieldError("zipCode", "guestDataVerificationInvalidZipCode");
        return false;
      }
      if (
        verificationType === VerificationType.MOBILE_NUMBER &&
        mobileNumber === ""
      ) {
        setFieldError(
          "mobileNumber",
          "guestDataVerificationInvalidMobileNumber"
        );
        return false;
      }
      if (verificationType === VerificationType.EMAIL && email === "") {
        setFieldError("email", "guestDataVerificationInvalidEmail");
        return false;
      }
    } else if (caseNumber.trim() === "") {
      setFieldError("caseNumber", "guestDataVerificationInvalidCaseNumber");
      return false;
    }
    return true;
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    clearErrors();
    const formData = new FormData(event.currentTarget);

    // DOB validation
    const monthValue = month?.value?.toString() ?? "";
    const dayValue = formData.get("dateOfBirthDay")?.toString() ?? "";
    const yearValue = formData.get("dateOfBirthYear")?.toString() ?? "";

    const isDOBValid = validateDOB(dayValue, monthValue, yearValue);
    if (!isDOBValid) {
      return;
    }

    // Additional data validation
    const caseNumber = formData.has("caseNumber")
      ? (formData.get("caseNumber") as string).trim()
      : "";
    const firstName = formData.has("firstName")
      ? (formData.get("firstName") as string)
      : "";
    const lastName = formData.has("lastName")
      ? (formData.get("lastName") as string)
      : "";
    const zipCode = formData.has("zipCode")
      ? (formData.get("zipCode") as string)
      : "";
    const mobileNumber = formData.has("mobileNumber")
      ? (formData.get("mobileNumber") as string)
      : "";
    const email = formData.has("email")
      ? (formData.get("email") as string)
      : "";
    if (
      !validateAdditionalUserData(
        caseNumber,
        firstName,
        lastName,
        zipCode,
        mobileNumber,
        email
      )
    ) {
      return;
    }

    const guestData: PublicGuestValidationInput = {
      month: monthValue.length === 1 ? "0" + monthValue : monthValue,
      day: dayValue.length === 1 ? "0" + dayValue : dayValue,
      year: yearValue,
      caseNumber,
      firstName,
      lastName,
      zipCode,
      mobilePhone: mobileNumber,
      email,
    };
    onSubmit(guestData);
  };

  const handleBackToLogin = () => {
    navigate(routes.logIn);
  };

  const handleBack = () => {
    setVerificationType(VerificationType.CASE_NUMBER);
  };

  const handleCaseNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const numericOnly = event.target.value.replace(/\D/g, "");
    event.target.value = numericOnly;
  };

  const caseNumberTextFormField: ReactElement = (
    <FormField
      label={intl.formatMessage(
        messages.billingCenterGuestVerificationLabelCaseNumber
      )}
      error={getValidationError("caseNumber")}
      htmlFor="caseNumber"
    >
      <Textfield
        id="caseNumber"
        name="caseNumber"
        data-testid="caseNumber"
        placeholder={intl.formatMessage(
          messages.billingCenterGuestVerificationLabelCaseNumberPlaceholder
        )}
        onChange={handleCaseNumberChange}
        type="text"
        maxLength={config.TEXT_FIELD_MAX_LENGTH}
        defaultValue=""
        inputMode="numeric"
        pattern="\d*"
        outline
        fullWidth
      />
    </FormField>
  );

  const firstNameTextFormField: ReactElement = (
    <FormField
      label={intl.formatMessage(
        messages.billingCenterGuestVerificationLabelFirstName
      )}
      error={getValidationError("firstName")}
      htmlFor="firstName"
    >
      <Textfield
        placeholder={intl.formatMessage(
          messages.billingCenterGuestVerificationLabelFirstName
        )}
        type="text"
        id="firstName"
        maxLength={config.TEXT_FIELD_MAX_LENGTH}
        name="firstName"
        data-testid="firstName"
        defaultValue=""
        outline
        fullWidth
      />
    </FormField>
  );

  const lastNameTextFormField: ReactElement = (
    <FormField
      label={intl.formatMessage(
        messages.billingCenterGuestVerificationLabelLastName
      )}
      error={getValidationError("lastName")}
      htmlFor="lastName"
    >
      <Textfield
        placeholder={intl.formatMessage(
          messages.billingCenterGuestVerificationLabelLastName
        )}
        type="text"
        id="lastName"
        maxLength={config.TEXT_FIELD_MAX_LENGTH}
        name="lastName"
        data-testid="lastName"
        defaultValue=""
        outline
        fullWidth
      />
    </FormField>
  );

  const zipCodeTextFormField: ReactElement = (
    <FormField
      label={intl.formatMessage(
        messages.billingCenterGuestVerificationTypeZipCodePlaceholder
      )}
      error={getValidationError("zipCode")}
      htmlFor="zipCode"
    >
      <Textfield
        placeholder={intl.formatMessage(
          messages.billingCenterGuestVerificationTypeZipCodePlaceholder
        )}
        type="text"
        id="zipCode"
        maxLength={config.TEXT_FIELD_MAX_LENGTH}
        name="zipCode"
        data-testid="zipCode"
        defaultValue=""
        outline
        fullWidth
      />
    </FormField>
  );

  const mobileNumberTextFormField: ReactElement = (
    <FormField
      label={intl.formatMessage(
        messages.billingCenterGuestVerificationTypeMobileNumberPlaceholder
      )}
      error={getValidationError("mobileNumber")}
      htmlFor="mobileNumber"
    >
      <Textfield
        placeholder={intl.formatMessage(
          messages.billingCenterGuestVerificationTypeMobileNumberPlaceholder
        )}
        type="text"
        id="mobileNumber"
        maxLength={config.TEXT_FIELD_MAX_LENGTH}
        name="mobileNumber"
        data-testid="mobileNumber"
        defaultValue=""
        outline
        fullWidth
      />
    </FormField>
  );

  const emailTextFormField: ReactElement = (
    <FormField
      label={intl.formatMessage(
        messages.billingCenterGuestVerificationTypeEmailPlaceholder
      )}
      error={getValidationError("email")}
      htmlFor="email"
    >
      <Textfield
        placeholder={intl.formatMessage(
          messages.billingCenterGuestVerificationTypeEmailPlaceholder
        )}
        type="text"
        id="email"
        maxLength={config.TEXT_FIELD_MAX_LENGTH}
        name="email"
        data-testid="email"
        defaultValue=""
        outline
        fullWidth
      />
    </FormField>
  );

  return (
    <div className="billing-center-guest-verification">
      <section className="billing-center-guest-verification__container">
        <Form
          onSubmit={handleSubmit}
          buttons={
            <>
              <ButtonNoShadow type="submit" raised loading={loading}>
                {intl.formatMessage(
                  messages.billingCenterGuestVerificationButtonContinue
                )}
              </ButtonNoShadow>

              {verificationType === VerificationType.CASE_NUMBER ? (
                <Button
                  type="button"
                  outlined
                  className="button"
                  onClick={handleBackToLogin}
                >
                  {intl.formatMessage(
                    messages.billingCenterGuestVerificationButtonLogin
                  )}
                </Button>
              ) : (
                <Button
                  type="button"
                  outlined
                  className="button"
                  onClick={handleBack}
                >
                  {intl.formatMessage(
                    messages.billingCenterGuestVerificationButtonBack
                  )}
                </Button>
              )}
            </>
          }
        >
          {verificationType !== VerificationType.CASE_NUMBER && (
            <div className="billing-center-guest-verification__container__non-case">
              {firstNameTextFormField}
              {lastNameTextFormField}
            </div>
          )}
          <div className="billing-center-guest-verification__container__dob">
            <DateOfBirthVerificationForm
              month={month}
              setMonth={setMonth}
              day={day}
              setDay={setDay}
              year={year}
              setYear={setYear}
              getValidationError={getValidationError}
              clearValidationError={clearValidationError}
              publicLogin
            />
          </div>
          {verificationType === VerificationType.CASE_NUMBER ? (
            <div className="billing-center-guest-verification__container__case">
              {caseNumberTextFormField}
              <div className="billing-center-guest-verification__container__case__label">
                <span className="billing-center-guest-verification__container__case__label__tooltip">
                  {intl.formatMessage(
                    messages.billingCenterGuestVerificationCaseNumberTooltip
                  )}
                </span>
                <p
                  className="billing-center-guest-verification__container__case__label__link"
                  onClick={() => setVerificationType(VerificationType.ZIPCODE)}
                >
                  {intl.formatMessage(
                    messages.billingCenterGuestVerificationCaseNumberLink
                  )}
                </p>
              </div>
            </div>
          ) : (
            <div className="billing-center-guest-verification__container__verification-type">
              <div>
                <span>
                  {intl.formatMessage(
                    messages.billingCenterGuestVerificationTypeLabel
                  )}
                </span>
              </div>
              <div className="billing-center-guest-verification__container__verification-type__action-chips">
                <ChipAction
                  onChange={() => setVerificationType(VerificationType.ZIPCODE)}
                  selected={verificationType === VerificationType.ZIPCODE}
                >
                  {intl.formatMessage(
                    messages.billingCenterGuestVerificationTypeZipCodePlaceholder
                  )}
                </ChipAction>
                <ChipAction
                  onChange={() =>
                    setVerificationType(VerificationType.MOBILE_NUMBER)
                  }
                  selected={verificationType === VerificationType.MOBILE_NUMBER}
                >
                  {intl.formatMessage(
                    messages.billingCenterGuestVerificationTypeMobileNumberPlaceholder
                  )}
                </ChipAction>

                <ChipAction
                  onChange={() => setVerificationType(VerificationType.EMAIL)}
                  selected={verificationType === VerificationType.EMAIL}
                >
                  {intl.formatMessage(
                    messages.billingCenterGuestVerificationTypeEmailPlaceholder
                  )}
                </ChipAction>
              </div>
              <div>
                {verificationType === VerificationType.ZIPCODE &&
                  zipCodeTextFormField}
                {verificationType === VerificationType.MOBILE_NUMBER &&
                  mobileNumberTextFormField}
                {verificationType === VerificationType.EMAIL &&
                  emailTextFormField}
              </div>
            </div>
          )}
        </Form>
      </section>
    </div>
  );
};

export default BillingCenterGuestVerification;
